import { AuthenticatedPhotoDirective } from '@shared/components/entity-photos/authenticated-photo.directive';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { FeedbackAccessService } from '@core/services/feedback-access.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApiConnectionState } from '@core/http/api-connection.interceptor';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../core/auth/auth.service';
import { UserFullInfoService } from '../../../../core/services/user-full-info.service';
import { UserFullInfoDto } from '@shared/models/user-full-info.model';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { AboutDialogComponent } from '../about-dialog/about-dialog.component';
import { GlobalSearchComponent } from '../../global-search/global-search.component';
import { modalDialogConfig } from '../../modal-shell/modal-dialog.config';

@Component({
  selector: 'app-header',
  imports: [AuthenticatedPhotoDirective, CommonModule, RouterModule, MatIconModule, MatButtonModule, MatDialogModule, MatMenuModule, MatDividerModule, MatProgressSpinnerModule, BreadcrumbComponent, GlobalSearchComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  
  readonly feedbackAccess = inject(FeedbackAccessService);
  readonly apiConnection = inject(ApiConnectionState);
  userFullInfo: UserFullInfoDto | null = null;
  isAuthenticated$!: Observable<boolean>;
  private destroy$ = new Subject<void>();

  constructor(
    public authService: AuthService,
    private userFullInfoService: UserFullInfoService,
    private changeDetector: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    this.isAuthenticated$ = this.authService.isAuthenticated$;
  }

  ngOnInit(): void {
    // Suscribirse al Observable para obtener los datos cuando estén disponibles
    this.userFullInfoService.getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          this.userFullInfo = user;
          this.changeDetector.markForCheck();
        },
        error: (error: unknown) => {
          console.error('Error obteniendo usuario:', error);
          this.changeDetector.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get preferredUsername(): string | null {
    return this.authService.decodedToken?.preferred_username ?? null;
  }
  canViewPermissionsReport(): boolean { return !!(this.userFullInfo?.isAdmin || this.userFullInfoService.hasPermission('ManageMinisterialActivity', this.userFullInfo)); }

  login(): void {
    this.authService.login(window.location.href).catch((error) => {
      console.error('Error iniciando sesión', error);
    });
  }

  logout(): void {
    this.userFullInfoService.clear();
    this.authService.logout().catch((error) => {
      console.error('Error cerrando sesión', error);
    });
  }

  formatUserLabel(user: UserFullInfoDto | null): string {
    if (!user) {
      return 'Usuario';
    }

    return user.fullName || 'Usuario';
  }

  openAboutDialog(): void {
    this.dialog.open(AboutDialogComponent, modalDialogConfig({ size: 'md', layout: 'summary' }));
  }

  getUserInitials(): string {
    const name = this.userFullInfo?.fullName?.trim();
    if (!name) return '?';
    const parts = name.split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
}
